import { RiErrorWarningLine } from "@remixicon/react";
import { Alert } from "@screener/uikit/components/alert";
import { Button } from "@screener/uikit/components/button";
import { Field, FieldError, FieldLabel } from "@screener/uikit/components/field";
import { Input } from "@screener/uikit/components/input";
import { type FormEvent, type ReactNode, useCallback, useRef, useState } from "react";
import { AuthHeading, AuthLayout, AuthStatus, SignInHint } from "@/auth/auth-layout";
import { ConfirmEmailScreen } from "@/auth/confirm-email-screen";
import { GoogleAlternative } from "@/auth/google-alternative";
import { InvalidInviteScreen } from "@/auth/invalid-invite-screen";
import type { InviteView } from "@/auth/invite-view";
import { TurnstileWidget, type TurnstileWidgetHandle } from "@/components/turnstile-widget";
import { useInviteView } from "@/hooks/use-invite-view";
import {
  type SignupCreated,
  type SignupRequest,
  type SignupResult,
  signup as signupRequest,
} from "@/lib/auth-api";

/** Исход отправки формы: ответ демона без успеха плюс отказы клиента до запроса. */
export type SignupFailure =
  | Exclude<SignupResult, SignupCreated>
  | "mismatch"
  | "bad-email"
  | "non-ascii-email";

/** Отказы клиента: запрос демону не уходил, токен капчи цел. */
function isClientFailure(result: SignupFailure | SignupCreated): boolean {
  return result === "mismatch" || result === "bad-email" || result === "non-ascii-email";
}

/**
 * Простая проверка почты до запроса: после обрезки пробелов по краям есть `@`,
 * нет пробелов внутри, только ASCII (демон отклоняет прочее, `400`). Остальное
 * проверяет демон.
 */
export function isPlausibleEmail(email: string): boolean {
  const trimmed = email.trim();
  return trimmed.includes("@") && !/\s/.test(trimmed) && isAscii(trimmed);
}

function isAscii(text: string): boolean {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: диапазон ASCII
  return !/[^\u0000-\u007f]/.test(text);
}

export interface SignupDraft {
  login: string;
  email: string;
  password: string;
  repeat: string;
  /** Токен ссылки-приглашения; `null` — открытая регистрация. */
  token: string | null;
  /** Токен виджета Turnstile; `null` — виджет его ещё не выдал или регистрация по ссылке. */
  turnstileToken: string | null;
}

/** Капча нужна только открытой регистрации: по ссылке виджета нет (§124 п. 3). */
function needsCaptcha(token: string | null): boolean {
  return token === null;
}

/** Готова ли форма к отправке по части капчи: по ссылке всегда, открытая — с токеном виджета. */
export function captchaReady(token: string | null, turnstileToken: string | null): boolean {
  return !needsCaptcha(token) || turnstileToken !== null;
}

/**
 * Токен Turnstile одноразовый: токен мог быть потрачен на любом ответе после
 * капчи, поэтому сброс — после любого неуспешного исхода, когда запрос ушёл
 * (stage9-signup-turnstile, Р4). Не сбрасывается успех и отказы клиента
 * (`mismatch`, `bad-email`, `non-ascii-email`): запрос не отправлялся.
 */
export function shouldResetCaptcha(result: SignupFailure | SignupCreated): boolean {
  return typeof result === "string" && !isClientFailure(result);
}

/**
 * Отправка формы: пароль и повтор сверяются здесь, при расхождении запрос не
 * уходит (spec `web-signup`, «Пароли не совпали»). Повтор на сервер не идёт.
 * Отправитель — параметр: проверяется без сети.
 */
export async function runSignup(
  draft: SignupDraft,
  send: (request: SignupRequest) => Promise<SignupResult> = signupRequest,
): Promise<SignupResult | "mismatch" | "bad-email" | "non-ascii-email"> {
  if (!isAscii(draft.email.trim())) {
    return "non-ascii-email";
  }
  if (!isPlausibleEmail(draft.email)) {
    return "bad-email";
  }
  if (draft.password !== draft.repeat) {
    return "mismatch";
  }
  const base = { login: draft.login, email: draft.email.trim(), password: draft.password };
  let request: SignupRequest;
  if (draft.token !== null) {
    request = { ...base, token: draft.token };
  } else if (draft.turnstileToken !== null) {
    request = { ...base, turnstile_token: draft.turnstileToken };
  } else {
    request = base;
  }
  return send(request);
}

/**
 * Текст отказа регистрации. `switch` с `satisfies never`: новый исход
 * `SignupResult` не соберётся, а не уйдёт к человеку без сообщения.
 */
export function describeSignupFailure(failure: SignupFailure): string {
  switch (failure) {
    case "mismatch":
      return "Пароли не совпадают";
    case "bad-email":
      return "Проверьте адрес почты";
    case "non-ascii-email":
      return "Адрес почты — только латинскими буквами, например name@mail.ru";
    case "invalid":
      return "Проверьте логин, почту и пароль: они не должны быть пустыми, логин — слишком длинным, почта — в виде имя@домен";
    case "email-taken":
      return "Эта почта уже зарегистрирована";
    case "email-mismatch":
      return "Эта ссылка выдана на другую почту. Укажите почту, на которую пришло приглашение.";
    case "disabled":
      return "Регистрация сейчас закрыта.";
    case "link-invalid":
      return "Ссылка недействительна: она истекла, уже использована или отозвана. Попросите новую.";
    case "login-taken":
      return "Этот логин занят. Выберите другой.";
    case "rate-limited":
      return "Слишком много попыток. Подождите и попробуйте снова.";
    case "busy":
      return "Сервер занят. Повторите чуть позже.";
    case "captcha-rejected":
      return "Проверка не пройдена. Пройдите её ещё раз.";
    case "captcha-unavailable":
      return "Проверка недоступна. Попробуйте зарегистрироваться позже.";
    case "unavailable":
      return "Демон не отвечает. Попробуйте позже.";
    default:
      return failure satisfies never;
  }
}

interface SignupFormProps {
  /** Токен ссылки-приглашения; `null` — открытая регистрация. */
  token: string | null;
  /** Ключ сайта Turnstile из `signup-options`; нужен открытой регистрации. */
  turnstileSiteKey: string | null;
  /**
   * «Вернуться ко входу» на экране «Подтвердите почту». Регистрация сессии не
   * выдаёт (stage9-email-confirmation, Р6): человек подтверждает почту и входит.
   */
  onBack: () => void;
}

/**
 * Страница регистрации (§104 пп. 7–8; stage9-auth-redesign, Д3). По ссылке
 * сначала спрашивает демона, действует ли она: пока ответа нет — левая
 * колонка без формы, «не действует» — экран «Ссылка недействительна»,
 * остальное (действует, предел проверок, сбой сети) — форма. Токен в
 * разметку не выводится. Открытая регистрация (без ссылки) несёт виджет
 * Turnstile: без его токена форма не отправляется (stage9-signup-turnstile).
 */
export function SignupForm({ token, turnstileSiteKey, onBack }: SignupFormProps) {
  const view = useInviteView(token);
  const [login, setLogin] = useState("");
  const [email, setEmail] = useState("");
  const [created, setCreated] = useState<{
    login: string;
    email: string;
    mailUnavailable: boolean;
  } | null>(null);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<SignupFailure | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [widgetFailed, setWidgetFailed] = useState(false);
  const widgetRef = useRef<TurnstileWidgetHandle>(null);

  const captcha = needsCaptcha(token);
  // Открытая регистрация без ключа сайта проверить человека не может — как
  // и виджет, который не загрузился.
  const captchaUnavailable = captcha && (turnstileSiteKey === null || widgetFailed);
  const ready = captchaReady(token, turnstileToken) && !captchaUnavailable;

  const handleToken = useCallback((next: string | null) => {
    setTurnstileToken(next);
    if (next !== null) {
      setWidgetFailed(false);
    }
  }, []);
  const handleWidgetUnavailable = useCallback(() => setWidgetFailed(true), []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (pending || !ready) {
        return;
      }
      const attemptedLogin = login;
      setPending(true);
      setFailure(null);
      const attemptedEmail = email.trim();
      runSignup({ login: attemptedLogin, email, password, repeat, token, turnstileToken })
        .then((result) => {
          // Расхождение паролей и негодная почта — опечатка: поля остаются, чтобы
          // поправить её; после ответа демона пароль со страницы убирается.
          if (result === "mismatch" || result === "bad-email" || result === "non-ascii-email") {
            setFailure(result);
            return;
          }
          setPassword("");
          setRepeat("");
          if (typeof result === "object") {
            // Показывается адрес, введённый в форме, а не маска демона.
            setCreated({
              login: attemptedLogin,
              email: attemptedEmail,
              mailUnavailable: result.mailUnavailable,
            });
            return;
          }
          if (captcha && shouldResetCaptcha(result)) {
            setTurnstileToken(null);
            widgetRef.current?.reset();
          }
          setFailure(result);
        })
        .finally(() => {
          setPending(false);
        });
    },
    [login, email, password, repeat, token, turnstileToken, pending, ready, captcha],
  );

  if (created !== null) {
    return (
      <AuthLayout>
        <ConfirmEmailScreen
          login={created.login}
          email={created.email}
          mailUnavailable={created.mailUnavailable}
          onBack={onBack}
        />
      </AuthLayout>
    );
  }

  return (
    <SignupLayout view={view}>
      <SignupFormView
        login={login}
        email={email}
        password={password}
        repeat={repeat}
        pending={pending}
        failure={failure}
        widgetUnavailable={captchaUnavailable}
        captchaReady={ready}
        captcha={
          captcha && turnstileSiteKey !== null ? (
            <TurnstileWidget
              siteKey={turnstileSiteKey}
              onToken={handleToken}
              onUnavailable={handleWidgetUnavailable}
              handleRef={widgetRef}
            />
          ) : null
        }
        onLoginChange={setLogin}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onRepeatChange={setRepeat}
        onSubmit={handleSubmit}
      />
    </SignupLayout>
  );
}

/** Левая колонка по виду страницы: ожидание, экран 2c или форма (экран 2b). */
export function SignupLayout({ view, children }: { view: InviteView; children: ReactNode }) {
  if (view === "checking") {
    return (
      <AuthLayout busy>
        <AuthStatus>Проверяем ссылку…</AuthStatus>
      </AuthLayout>
    );
  }
  if (view === "invalid") {
    return (
      <AuthLayout>
        <InvalidInviteScreen />
      </AuthLayout>
    );
  }
  return <AuthLayout>{children}</AuthLayout>;
}

interface SignupFormViewProps {
  login: string;
  email: string;
  password: string;
  repeat: string;
  pending: boolean;
  failure: SignupFailure | null;
  /** Виджет капчи между полями и кнопкой; нет — регистрация по ссылке. */
  captcha?: ReactNode;
  /** Выдан ли токен капчи (по ссылке капчи нет — `true`). */
  captchaReady?: boolean;
  /** Проверка недоступна (скрипт или виджет): сообщение над полями, ошибка поля от демона остаётся. */
  widgetUnavailable?: boolean;
  onLoginChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onRepeatChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

/**
 * Форма регистрации (экран 2b). Отказы сервера — `Alert` над полями, того же
 * вида, что на форме входа. `link-invalid` — ссылка перестала действовать
 * на открытой форме: поля и кнопки неактивны. У поля остаются только то, что
 * относится к нему: `login-taken` — у логина, «Пароли не совпадают» — у повтора.
 */
export function SignupFormView({
  login,
  email,
  password,
  repeat,
  pending,
  failure,
  captcha = null,
  captchaReady: captchaPassed = true,
  widgetUnavailable = false,
  onLoginChange,
  onEmailChange,
  onPasswordChange,
  onRepeatChange,
  onSubmit,
}: SignupFormViewProps) {
  const failureMessage = failure === null ? null : describeSignupFailure(failure);
  const linkInvalid = failure === "link-invalid";
  const locked = pending || linkInvalid;
  const repeatFailed = failure === "mismatch";
  // 400 демон не разбирает по полям: он остаётся общим алертом над формой, а
  // поля почты и пароля им не помечаются.
  const emailFailed =
    failure === "email-taken" ||
    failure === "email-mismatch" ||
    failure === "bad-email" ||
    failure === "non-ascii-email";
  const fieldFailure =
    failure === "login-taken" ||
    failure === "mismatch" ||
    failure === "email-taken" ||
    failure === "email-mismatch" ||
    failure === "bad-email" ||
    failure === "non-ascii-email";
  const alertMessage = widgetUnavailable
    ? describeSignupFailure("captcha-unavailable")
    : failureMessage && !fieldFailure
      ? failureMessage
      : null;

  return (
    // `noValidate`: почту проверяет `isPlausibleEmail`, а не подсказка браузера.
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6" aria-label="Регистрация">
      <AuthHeading title="Регистрация в kelv">
        <SignInHint />
      </AuthHeading>
      {alertMessage ? (
        <Alert variant="error" size="sm">
          <RiErrorWarningLine aria-hidden="true" />
          <span>{alertMessage}</span>
        </Alert>
      ) : null}
      <div className="flex flex-col gap-3.5">
        <Field invalid={failure === "login-taken"}>
          <FieldLabel className="text-xs">Логин</FieldLabel>
          <Input
            size="lg"
            name="login"
            autoComplete="username"
            value={login}
            onChange={(event) => onLoginChange(event.target.value)}
            disabled={locked}
            required
          />
          {failure === "login-taken" && failureMessage ? (
            <FieldError match>{failureMessage}</FieldError>
          ) : null}
        </Field>
        <Field invalid={emailFailed}>
          <FieldLabel className="text-xs">Почта</FieldLabel>
          <Input
            size="lg"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            disabled={locked}
            required
          />
          {emailFailed && failureMessage ? <FieldError match>{failureMessage}</FieldError> : null}
        </Field>
        <Field>
          <FieldLabel className="text-xs">Пароль</FieldLabel>
          <Input
            size="lg"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            disabled={locked}
            required
          />
        </Field>
        <Field invalid={repeatFailed}>
          <FieldLabel className="text-xs">Повтор пароля</FieldLabel>
          <Input
            size="lg"
            name="repeat"
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(event) => onRepeatChange(event.target.value)}
            disabled={locked}
            required
          />
          {repeatFailed && failureMessage ? <FieldError match>{failureMessage}</FieldError> : null}
        </Field>
      </div>
      {captcha}
      <Button type="submit" size="lg" className="w-full" disabled={locked || !captchaPassed}>
        {pending ? "Регистрация…" : "Зарегистрироваться"}
      </Button>
      <GoogleAlternative />
    </form>
  );
}
